import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, PhoneCall, CheckCircle, MessageSquare, UserCog, Send, ShieldCheck, Users, Trash2 } from "lucide-react";
import { BulkSelectBar, RowCheckbox } from "../components/BulkSelectBar";

const STATUS_VISUAL = {
  new: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  called: "bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]",
  contacted: "bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]",
  interested: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  visit: "bg-[#FCE7F3] text-[#9D174D] border-[#FBCFE8]",
  closed: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]",
  not_interested: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

export default function EnquiriesTab() {
  const [enquiries, setEnquiries] = useState([]);
  const [team, setTeam] = useState([]);
  // v2.48 — Bulk select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = async () => {
    const [r, t] = await Promise.all([
      api.get("/admin/enquiries"),
      api.get("/admin/team-members").catch(() => ({ data: [] })),
    ]);
    setEnquiries(r.data);
    setTeam((t.data || []).filter((m) => m.status === "active"));
  };
  useEffect(() => { load(); }, []);

  // v2.72 — Live sync (instant refresh after any enquiry/lead mutation,
  // incl. new enquiries pushed via the WS-notification piggyback) + 45 s
  // visibility-aware background poll.
  useDataSync(["enquiries"], load);
  useAutoRefresh(load, 45000);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/enquiries/${id}/status`, { status });
      toast.success("Updated");
      emitDataChange(["enquiries", "leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const assign = async (id, team_member_id) => {
    try {
      await api.post(`/admin/leads/${id}/assign`, { team_member_id: team_member_id || null });
      toast.success(team_member_id ? "Lead assigned" : "Unassigned");
      emitDataChange(["enquiries", "leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const autoAssignAll = async () => {
    try {
      const r = await api.post("/admin/leads/auto-assign");
      toast.success(`Round-robin assigned ${r.data.assigned} leads to ${r.data.members} members`);
      emitDataChange(["enquiries", "leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  // v2.48 — Single + bulk delete
  const deleteOne = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Enquiry deleted");
      // v2.72 — Optimistic removal + bus announce (subscription reconciles).
      setEnquiries((prev) => prev.filter((e2) => e2.id !== id));
      emitDataChange(["enquiries", "leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err, "Failed")); }
  };
  const bulkDelete = async (ids) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/enquiries/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} enquir${r.data.deleted === 1 ? "y" : "ies"}`);
      // v2.72 — Optimistic removal + bus announce (subscription reconciles).
      setEnquiries((prev) => prev.filter((e2) => !ids.includes(e2.id)));
      emitDataChange(["enquiries", "leads", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err, "Bulk delete failed")); }
    setBulkBusy(false);
  };

  if (!enquiries.length) {
    return (
      <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm" data-testid="enquiries-empty">
        <MessageSquare size={28} className="mx-auto mb-2 text-[#D4D4D8]" />
        No enquiries yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-white border border-[#E4E4E7] rounded-sm p-3" data-testid="enq-toolbar">
        <div className="text-[12px] text-[#52525B]">
          <b className="text-[#09090B]">{enquiries.length}</b> total leads · <b className="text-[#0055FF]">{enquiries.filter(e => !e.assigned_to).length}</b> unassigned
        </div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={autoAssignAll} data-testid="enq-auto-assign-btn">
          <UserCog size={13} className="mr-1.5" /> Auto-assign (round-robin)
        </Button>
      </div>
      {/* v2.48 — Bulk select */}
      <BulkSelectBar
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        allIds={enquiries.map((e) => e.id)}
        onBulkDelete={bulkDelete}
        busy={bulkBusy}
        noun="enquiries"
        testidPrefix="enq-bulk"
      />
      {enquiries.map((e) => (
        <div key={e.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`enquiry-${e.id}`}>
          <div className="flex items-start gap-3">
            <RowCheckbox
              id={e.id}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              testid={`enq-row-check-${e.id}`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[300px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-heading font-semibold text-[15px] tracking-tight">{e.name}</h4>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm ${STATUS_VISUAL[e.status] || STATUS_VISUAL.new}`}>
                      {e.status}
                    </span>
                  </div>
                  <div className="text-[12px] text-[#52525B] mt-1 flex flex-wrap gap-3">
                    <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-[#0055FF] font-semibold"><Phone size={11} /> {e.phone}</a>
                    {e.user_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {e.user_email}</span>}
                  </div>
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Property</div>
                    <div className="text-[13px] text-[#09090B]">{e.property_title} <span className="text-[#A1A1AA]">· {e.property_area}</span></div>
                  </div>
                  {e.message && <div className="text-[12px] text-[#52525B] italic mt-1">"{e.message}"</div>}
                  <div className="text-[11px] text-[#A1A1AA] mt-1">{new Date(e.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2 items-start">
                  <a href={`tel:${e.phone}`}>
                    <Button size="sm" className="btn-brand rounded-sm" data-testid={`call-${e.id}`}>
                      <PhoneCall size={12} className="mr-1.5" /> Call
                    </Button>
                  </a>
                  <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                    <SelectTrigger className="rounded-sm w-32" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="called">Called</SelectItem>
                      <SelectItem value="interested">Interested</SelectItem>
                      <SelectItem value="visit">Site Visit</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="not_interested">Not Interested</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={e.assigned_to || "_none"} onValueChange={(v) => assign(e.id, v === "_none" ? null : v)}>
                    <SelectTrigger className="rounded-sm w-40" data-testid={`enq-assign-${e.id}`}>
                      <SelectValue placeholder="Assign…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Unassigned</SelectItem>
                      {team.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{`${m.name} · ${m.sub_role}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteOne(e.id)}
                    className="h-8 px-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                    title="Delete enquiry"
                    data-testid={`enq-delete-${e.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              {/* Notes timeline + admin note input */}
              <LeadNotes lead={e} onChanged={load} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadNotes({ lead, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const notes = lead.notes || [];

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/leads/${lead.id}/note`, { note: text.trim() });
      setText("");
      toast.success("Note added");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Failed to add note"));
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#F4F4F5]" data-testid={`lead-notes-${lead.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] uppercase tracking-wider font-semibold text-[#52525B] hover:text-[#0055FF] inline-flex items-center gap-1.5"
        data-testid={`lead-notes-toggle-${lead.id}`}
      >
        <MessageSquare size={12} /> Notes ({notes.length}) {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {notes.length === 0 && <div className="text-[12px] text-[#A1A1AA] italic">No notes yet — add the first one.</div>}
          {notes.map((n) => (
            <div
              key={n.id || n.at}
              className={`text-[12px] rounded-sm p-2 border ${n.by_role === "admin" ? "bg-[#FEF3C7] border-[#FDE68A]" : "bg-[#EEF4FF] border-[#C7D9FF]"}`}
              data-testid={`lead-note-${n.id || n.at}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-0.5">
                {n.by_role === "admin" ? <ShieldCheck size={10} /> : <Users size={10} />}
                <span className={n.by_role === "admin" ? "text-[#92400E]" : "text-[#0044CC]"}>
                  {n.by_role === "admin" ? "Admin" : "Team"}
                </span>
                <span className="text-[#71717A] normal-case tracking-normal">· {n.by_name || "—"}</span>
                <span className="text-[#A1A1AA] ml-auto normal-case tracking-normal">{n.at ? new Date(n.at).toLocaleString() : ""}</span>
              </div>
              <div className="text-[#3F3F46] whitespace-pre-line">{n.text}</div>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Input
              value={text}
              onChange={(ev) => setText(ev.target.value)}
              placeholder="Add an admin note…"
              className="rounded-sm text-[13px]"
              onKeyDown={(ev) => { if (ev.key === "Enter") save(); }}
              data-testid={`lead-note-input-${lead.id}`}
            />
            <Button onClick={save} disabled={busy || !text.trim()} className="btn-brand rounded-sm" size="sm" data-testid={`lead-note-save-${lead.id}`}>
              <Send size={12} className="mr-1" /> {busy ? "Saving…" : "Add"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
